// useWishlistSync.js - Hook to sync wishlist with MongoDB

import { useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import useStore from '../store/useStore';
import { fetchWishlist } from './wishlistApi';

/**
 * Hook to sync wishlist from MongoDB when user logs in
 * Call this in a top-level component (e.g., App.js or Layout)
 */
const useWishlistSync = () => {
  const { user, isLoaded } = useUser();
  const { setWishlist, wishlistLoaded, clearWishlist } = useStore();

  useEffect(() => {
    const syncWishlist = async () => {
      // Wait for Clerk to load
      if (!isLoaded) return;

      // If user logged out, clear wishlist
      if (!user?.id) {
        if (wishlistLoaded) {
          clearWishlist();
        }
        return;
      }

      // Skip if already loaded
      if (wishlistLoaded) return;

      try {
        const items = await fetchWishlist(user.id);

        // Map API format to store format
        const mappedItems = items.map(item => ({
          id: item.tripId,
          stateName: item.stateName,
          startDate: item.startDate,
          endDate: item.endDate,
          placesCount: item.placesCount,
          activitiesCount: item.activitiesCount,
          image: item.image,
          itineraryData: item.itineraryData,
          createdAt: item.createdAt,
        }));

        setWishlist(mappedItems);
        // console.log('✅ Wishlist synced:', mappedItems.length, 'items');
      } catch (error) {
        // console.error('❌ Failed to sync w/ishlist:', error);
        // Don't clear local data on error - user might have offline items
        throw new Error('❌ Failed to sync wishlist:');
      }
    };

    syncWishlist();
  }, [isLoaded, user?.id, wishlistLoaded, setWishlist, clearWishlist]);

  return { wishlistLoaded };
};

export default useWishlistSync;
